import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";

function started(req) {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  const now = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  return `${req.date_from || ""} ${req.time_from || "00:00"}` <= now;
}

function RestoreChoice({ restore, setRestore }) {
  const opt = (v, l, d) => (
    <button type="button" onClick={() => setRestore(v)} data-testid={`cancel-restore-${v}`}
      className={`text-left rounded-lg border p-2.5 text-xs transition-colors ${restore === v ? "border-[#1C352D] bg-stone-50" : "border-stone-200 hover:bg-stone-50"}`}>
      <div className="font-medium text-sm">{l}</div><div className="text-stone-500">{d}</div>
    </button>
  );
  return (
    <div className="grid grid-cols-2 gap-2">
      {opt("presente", "Presente", "lo studente era a lezione")}
      {opt("assente", "Assente", "non giustificato, da verificare")}
    </div>
  );
}

export function CancelLeaveCard({ req, student }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [restore, setRestore] = useState("presente");
  const active = req.category === "permesso_assenza" && ["pending", "approvato"].includes(req.status);
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/requests/${req.id}/cancel`, { restore, note })).data,
    onSuccess: (d) => {
      toast.success(`Permesso ${student ? "ritirato" : "revocato"}${d.refunded_hours ? ` · ${d.refunded_hours}h restituite` : ""}${d.restored_lessons ? ` · ${d.restored_lessons} lezioni ripristinate` : ""}`);
      ["request", "requests", "my-permessi-requests", "my-leave-quota", "requests-stats", "unjustified"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  if (!active) return null;
  const locked = student && req.status === "approvato" && started(req);
  const approved = req.status === "approvato";
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none space-y-3" data-testid="cancel-leave-card">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{student ? "Non ti serve più?" : "Revoca permesso"}</div>
      {locked ? (
        <p className="text-xs text-stone-500" data-testid="cancel-leave-locked">Il permesso è già iniziato: per annullarlo scrivi alla segreteria nella conversazione.</p>
      ) : (
        <>
          <p className="text-xs text-stone-500">
            {approved ? `${req.medical ? "" : "Le ore tornano nel monte ore e "}le lezioni giustificate da questo permesso vengono ripristinate.` : "La richiesta viene annullata."}
          </p>
          <Button variant="outline" className="w-full rounded-full border-red-300 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="cancel-leave-btn">
            <Undo2 size={14} className="mr-1.5" /> {student ? "Ritira il permesso" : "Revoca permesso"}
          </Button>
        </>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="cancel-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{student ? "Ritirare il permesso?" : "Revocare il permesso?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {approved && !req.medical && <><b>{req.requested_hours || 0}h</b> tornano nel monte ore. </>}
              {approved && (student ? "Nelle lezioni del permesso risulterai presente." : "Scegli come registrare le lezioni coperte dal permesso:")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {approved && !student && <RestoreChoice restore={restore} setRestore={setRestore} />}
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (facoltativa)" data-testid="cancel-leave-note" />
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" disabled={mut.isPending}
              onClick={(e) => { e.preventDefault(); mut.mutate(); }} data-testid="cancel-leave-confirm">
              {student ? "Ritira" : "Revoca"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
